import {
  useAccessControl,
  useAccessibleBusinesses,
  useCurrentBusinessOutlets,
  useCurrentUser,
  useUserAuth,
} from '@billx/api';
import { Box, Divider, MenuItem, Typography } from '@mui/material';
import {
  BoltIcon,
  Building2Icon,
  LogOutIcon,
  UserRoundIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CustomMenu, UtilityIconButton } from '@/components/common';

export const ProfileSection = () => {
  const navigate = useNavigate();
  const { canAccessPage } = useAccessControl();
  const { accessibleBusinesses } = useAccessibleBusinesses();
  const { outlets } = useCurrentBusinessOutlets();
  const { updateUserPreference } = useCurrentUser();
  const { logout } = useUserAuth();
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(
    null
  );

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/staff/profile');
    handleProfileMenuClose();
  };

  const handleBusinessClick = async () => {
    await updateUserPreference.mutateAsync({ selectedBusinessId: null });
    handleProfileMenuClose();
  };

  const handleOutletSwitch = async () => {
    // await updateUserPreference.mutateAsync({ selectedOutletId: null });
    handleProfileMenuClose();
    navigate('/user/select');
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <UtilityIconButton
          onClick={handleProfileMenuOpen}
          tooltip={{ title: 'Profile', placement: 'right' }}
        >
          <UserRoundIcon size={20} />
        </UtilityIconButton>
      </Box>

      {/* Profile Menu */}
      <CustomMenu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            ml: 1.5,
            mt: 1,
            minWidth: 150,
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {[
          {
            type: 'button',
            label: 'Profile',
            onClick: handleProfileClick,
            visible: canAccessPage('staff.profile'),
            icon: <UserRoundIcon size={18} />,
          },
          {
            type: 'button',
            label: 'Switch Business',
            onClick: handleBusinessClick,
            icon: <Building2Icon size={18} />,
            visible: accessibleBusinesses.length > 1,
          },
          {
            type: 'button',
            label: 'Switch Outlet',
            onClick: handleOutletSwitch,
            icon: <BoltIcon size={18} />,
            visible: outlets.length > 1,
          },
          {
            type: 'divider',
            label: 'divider1',
          },
          {
            type: 'button',
            label: 'Sign Out',
            onClick: handleSignOut,
            color: 'error.main',
            visible: true,
            icon: <LogOutIcon size={16} />,
          },
        ].map((item) => {
          if (item.type === 'divider') {
            return <Divider key={item.label} />;
          }
          if (!item.visible) {
            return null;
          }
          return (
            <MenuItem
              key={item.label}
              onClick={item.onClick}
              sx={{
                py: { xs: 0.5, md: 1.5 },
                color: item.color,
              }}
            >
              {item.icon}
              <Typography variant="body2" ml={1}>
                {item.label}
              </Typography>
            </MenuItem>
          );
        })}
      </CustomMenu>
    </>
  );
};
